/**
 * Auto-scrolling proof bandeau that stays scrollable by hand.
 * CSS :hover/:focus-within pause is avoided: a focused link after
 * target=_blank would freeze the ticker until the next click.
 */
(() => {
  if (customElements.get('gb-marquee')) return;

  class GbMarquee extends HTMLElement {
    connectedCallback() {
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.hovering = false;
      this.moved = false;
      this.dragging = false;
      this.raf = 0;
      this.last = 0;
      this.dragX = 0;
      this.dragScroll = 0;
      this.speed = 28;

      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onHoverMove = this.onHoverMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.tick = this.tick.bind(this);

      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('pointermove', this.onHoverMove);
      this.addEventListener('wheel', this.onWheel, { passive: false });
      this.addEventListener('click', this.onClick, true);
      document.addEventListener('visibilitychange', this.onVisibility);

      if (!this.reduce) this.play();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.releaseDrag();
    }

    get loopWidth() {
      const first = this.querySelector('.gb-proof__certs');
      return first?.offsetWidth || 0;
    }

    play() {
      if (this.reduce || this.raf) return;
      this.last = 0;
      this.raf = window.requestAnimationFrame(this.tick);
    }

    stop() {
      if (!this.raf) return;
      window.cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    pause() {
      this.hovering = true;
    }

    resume() {
      this.hovering = false;
      this.dragging = false;
      this.play();
    }

    blurLinks() {
      const active = document.activeElement;
      if (active && this.contains(active) && typeof active.blur === 'function') {
        active.blur();
      }
    }

    normalize() {
      const loop = this.loopWidth;
      if (!loop) return;
      while (this.scrollLeft >= loop) this.scrollLeft -= loop;
      while (this.scrollLeft < 0) this.scrollLeft += loop;
    }

    tick(now) {
      this.raf = window.requestAnimationFrame(this.tick);
      if (this.hovering || this.dragging || document.visibilityState !== 'visible') {
        this.last = now;
        return;
      }
      const dt = this.last ? Math.min(48, now - this.last) : 16;
      this.last = now;
      this.scrollLeft += (this.speed * dt) / 1000;
      this.normalize();
    }

    onHoverMove() {
      this.hovering = true;
    }

    onPointerEnter() {
      this.hovering = true;
    }

    onPointerLeave() {
      this.hovering = false;
      this.dragging = false;
      this.normalize();
    }

    onPointerDown(event) {
      if (event.button != null && event.button !== 0) return;
      this.moved = false;
      this.hovering = true;

      if (event.pointerType === 'touch') return;

      this.dragging = true;
      this.dragX = event.clientX;
      this.dragScroll = this.scrollLeft;
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      const dx = event.clientX - this.dragX;
      if (!this.moved && Math.abs(dx) < 8) return;
      this.moved = true;
      this.scrollLeft = this.dragScroll - dx;
      this.normalize();
    }

    onPointerUp() {
      this.releaseDrag();
      if (!this.matches(':hover')) this.hovering = false;
    }

    releaseDrag() {
      this.dragging = false;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    onWheel(event) {
      const dx = event.deltaX;
      const dy = event.deltaY;
      if (dx === 0 && dy === 0) return;
      event.preventDefault();
      this.hovering = true;
      this.scrollLeft += Math.abs(dx) > Math.abs(dy) ? dx : dy;
      this.normalize();
    }

    onClick(event) {
      if (this.moved) {
        event.preventDefault();
        event.stopPropagation();
        this.moved = false;
        return;
      }
      const link = event.target.closest('a');
      window.setTimeout(() => {
        link?.blur();
        this.blurLinks();
      }, 0);
    }

    onVisibility() {
      if (document.visibilityState !== 'visible') {
        this.last = 0;
        return;
      }
      this.blurLinks();
      this.moved = false;
      this.dragging = false;
      this.hovering = false;
      this.play();
    }
  }

  customElements.define('gb-marquee', GbMarquee);
})();
